// components/message-row.js
// ═══════════════════════════════════════════════════════════
// Single message row. Owns hover state for reaction bar.
// Isolated — one message hovering never affects another.
// ═══════════════════════════════════════════════════════════
import { useState, memo } from 'react';
import MessageContent from './message-content';
import ReactionBar from './reaction-bar';

// Exported so other chat features (e.g. space, which has custom inline
// message rendering instead of using MessageRow) can show the same
// streaming indicator + static logo with hover-tooltip greeting.
export function StreamingIndicator() {
  return (
    <div style={{ width: 40, height: 40, animation: 'shiftPulse 1.8s ease-in-out infinite' }}>
      <svg viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg" width="40" height="40">
        <circle cx="60" cy="60" r="46" stroke="#7A9E7E" strokeWidth="1.0" opacity="0.15"/>
        <circle cx="60" cy="60" r="38" stroke="#C8A96E" strokeWidth="1.2" opacity="0.22"/>
        <circle cx="60" cy="60" r="28" stroke="#7A9E7E" strokeWidth="1.5" opacity="0.38"/>
        <circle cx="60" cy="60" r="18" stroke="#C8A96E" strokeWidth="1.8" opacity="0.58"/>
        <circle cx="60" cy="60" r="10" stroke="#7A9E7E" strokeWidth="2.2" opacity="0.7"/>
        <circle cx="60" cy="60" r="8" fill="#E8C99A" opacity="0.18"/>
        <circle cx="60" cy="60" r="5" fill="#E8C99A"/>
        <circle cx="60" cy="60" r="3" fill="#F0EDE8"/>
      </svg>
      <style jsx>{`
        @keyframes shiftPulse {
          0%, 100% { opacity: 0.6; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.08); }
        }
      `}</style>
    </div>
  );
}

export function ShiftStaticLogo({ userRecord }) {
  const [showTip, setShowTip] = useState(false);
  const firstName = userRecord?.first_name || '';
  const aiName = userRecord?.ai_name || 'Shift';
  const greeting = firstName
    ? `Hello ${firstName}, I'm ${aiName}.`
    : `I'm ${aiName}.`;

  return (
    <div
      style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 10, marginTop: 8, pointerEvents: 'auto' }}
      onMouseEnter={() => setShowTip(true)}
      onMouseLeave={() => setShowTip(false)}
    >
      <svg viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg" width="60" height="60" style={{ cursor: 'default', opacity: 0.85 }}>
        <circle cx="60" cy="60" r="46" stroke="#7A9E7E" strokeWidth="1.0" opacity="0.15"/>
        <circle cx="60" cy="60" r="38" stroke="#C8A96E" strokeWidth="1.2" opacity="0.22"/>
        <circle cx="60" cy="60" r="28" stroke="#7A9E7E" strokeWidth="1.5" opacity="0.38"/>
        <circle cx="60" cy="60" r="18" stroke="#C8A96E" strokeWidth="1.8" opacity="0.58"/>
        <circle cx="60" cy="60" r="10" stroke="#7A9E7E" strokeWidth="2.2" opacity="0.7"/>
        <circle cx="60" cy="60" r="8" fill="#E8C99A" opacity="0.18"/>
        <circle cx="60" cy="60" r="5" fill="#E8C99A"/>
        <circle cx="60" cy="60" r="3" fill="#F0EDE8"/>
      </svg>
      {showTip && (
        <div style={{
          background: '#1e1e2a', border: '1px solid #2a2a2a',
          borderRadius: 20, padding: '6px 14px',
          whiteSpace: 'nowrap', pointerEvents: 'none',
          fontSize: 12, color: '#888',
          fontFamily: 'Georgia, serif', fontStyle: 'italic',
          boxShadow: '0 4px 16px rgba(0,0,0,0.5)',
        }}>
          {greeting}
        </div>
      )}
    </div>
  );
}

function MessageRow({ m, i, isLastUser, lastUserMsgRef, userRecord }) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      ref={isLastUser ? lastUserMsgRef : null}
      data-msg-role={m.role}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        marginBottom: 20,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: m.role === 'user' ? 'flex-end' : 'flex-start',
      }}
    >
      {/* User messages */}
      {m.role === 'user' && (
        <>
          <div style={{
            maxWidth: '600px',
            width: 'fit-content',
            fontSize: 'var(--msg-body-size, 14px)',
            lineHeight: 1.65,
            textAlign: 'left',
            whiteSpace: 'pre-wrap',
            color: '#e0e0e0',
            background: '#111118',
            borderRadius: '12px 12px 2px 12px',
            padding: '12px 16px',
            border: '1px solid #222',
          }}>
            <MessageContent m={m} i={i} />
          </div>
          <div style={{ opacity: hovered ? 1 : 0, transition: 'opacity 0.15s' }}>
            <ReactionBar
              message={m}
              onCopy={() => {}}
              onPin={() => {}}
              onIdea={() => {}}
              onRegenerate={() => {}}
              onFlag={() => {}}
              onEdit={() => {}}
            />
          </div>
        </>
      )}

      {/* Assistant messages */}
      {m.role === 'assistant' && (
        <div style={{
          maxWidth: '600px',
          width: '100%',
          fontSize: 'var(--msg-body-size, 14px)',
          lineHeight: 1.65,
          textAlign: 'left',
          whiteSpace: 'pre-wrap',
          color: '#bbb',
          background: 'none',
          borderRadius: '0',
          padding: '0',
        }}>
          {m.streaming && !m.content ? (
            <StreamingIndicator />
          ) : m.streaming && m.content ? (
            <>
              <MessageContent m={m} i={i} />
              <StreamingIndicator />
            </>
          ) : (
            <>
              <MessageContent m={m} i={i} />
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <div style={{ opacity: hovered ? 1 : 0, transition: 'opacity 0.15s' }}>
                  <ReactionBar
                    message={m}
                    onCopy={() => {}}
                    onPin={() => {}}
                    onIdea={() => {}}
                    onRegenerate={() => {}}
                    onFlag={() => {}}
                    onEdit={() => {}}
                  />
                </div>
                <ShiftStaticLogo userRecord={userRecord} />
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default memo(MessageRow, (prev, next) => {
  return (
    prev.m.content === next.m.content &&
    prev.m.streaming === next.m.streaming &&
    prev.i === next.i
  );
});